import { useNavigate } from 'react-router-dom';
import { MapPin, Users } from 'lucide-react';
import { ParticipantsState } from '../../history/components/HistoryStats';
import { getTypeLabel } from '../../../utils/workshopMigration';
import { formatClosingDate } from '../../../utils/workshopClosing';
import { longDate, UpcomingRow } from '../utils/dashboardData';

/**
 * Le seul endroit de l'application où l'on voit le remplissage RÉEL d'un
 * atelier à venir : /workshops affiche `remainingPlaces`, qui dérive, et
 * l'Historique ne regarde que le passé. Trié par remplissage croissant, le pire
 * atelier est en première ligne — la décision se prend sans lire la liste.
 */

/** Au-delà, la liste cesse d'être une file d'attente et redevient un catalogue. */
const MAX_ROWS = 6;

interface Props {
  rows: UpcomingRow[];
  /** Ateliers proches devenus inactionnables : comptés, pas listés. */
  excludedCount: number;
  participantsState: ParticipantsState;
  /** Requête ateliers en vol : interdit d'affirmer « aucun atelier ouvert ». */
  loading: boolean;
  className?: string;
}

const RowsSkeleton = () => (
  <div className="flex flex-col gap-3 px-6 py-4">
    {[...Array(3)].map((_, i) => (
      <div
        key={i}
        className="h-24 w-full rounded bg-shimmer bg-[length:400%_100%] animate-shimmer"
      />
    ))}
  </div>
);

const UpcomingFillPanel = ({
  rows,
  excludedCount,
  participantsState,
  loading,
  className,
}: Props) => {
  const navigate = useNavigate();
  const ready = participantsState === 'ready';
  const visible = rows.slice(0, MAX_ROWS);

  return (
    <section
      className={`rounded-2xl border border-[#623413]/10 bg-white shadow-sm ${
        className || ''
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-6 py-4">
        <h3 className="font-semibold text-[#623413]">À remplir</h3>
        <span className="text-sm text-gray-500">
          réservations encore ouvertes, 30 prochains jours
        </span>
      </div>

      {/* Une liste vide pendant le chargement dirait « rien à vendre » : le même
          mensonge qu'un faux zéro, porté par une phrase. */}
      {loading ? (
        <RowsSkeleton />
      ) : visible.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-500">
          Aucun atelier ouvert aux réservations dans les 30 prochains jours.
        </p>
      ) : (
        <div className="flex flex-col gap-3 px-6 py-4">
          {visible.map((row) => {
            const { workshop } = row;
            const empty = ready && row.places === 0;

            return (
              <button
                key={workshop.id}
                type="button"
                onClick={() => navigate('/workshops')}
                className={`w-full text-left flex items-center justify-between gap-4 bg-white p-4 rounded shadow-sm border transition hover:border-[#623413]/30 hover:shadow ${
                  empty ? 'border-amber-200' : 'border-[#623413]/10'
                }`}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <img
                    src={workshop.imageUrl || '/defaultimages/defaultcake.png'}
                    alt={workshop.title}
                    className="h-16 w-16 shrink-0 object-cover rounded border border-[#623413]/20"
                  />
                  <div className="flex flex-col min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-base font-semibold text-[#623413] truncate">
                        {workshop.title}
                      </p>
                      <span className="shrink-0 rounded-full bg-[#623413]/5 px-2 py-0.5 text-xs text-[#623413]">
                        {getTypeLabel(workshop.type)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 first-letter:uppercase">
                      {longDate(workshop.date.toDate())}
                    </p>
                    {workshop.location && (
                      <p className="flex items-center gap-1 text-sm text-gray-500">
                        <MapPin size={13} className="shrink-0" />
                        <span className="truncate">{workshop.location}</span>
                      </p>
                    )}
                    {/* « À remplir » ne devient une décision qu'accompagné de
                        « il te reste X heures ». */}
                    {row.closesAt && (
                      <p className="mt-1 text-xs text-gray-400">
                        ferme {formatClosingDate(row.closesAt)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col items-end gap-1 shrink-0 w-40">
                  <p className="flex items-center gap-1.5 text-sm font-medium text-[#623413]">
                    <Users size={15} />
                    {ready ? row.places : '—'}
                    {row.capacity > 0 && (
                      <span className="font-normal text-gray-400">
                        / {row.capacity}
                      </span>
                    )}
                  </p>
                  {ready && row.capacity > 0 && (
                    <>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-100">
                        <div
                          className="h-full rounded-full bg-[#623413]"
                          style={{ width: `${row.fillRate}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-400">
                        {row.fillRate} % de remplissage
                      </p>
                    </>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 px-6 py-3 text-xs text-gray-400">
        <div className="flex flex-col gap-0.5">
          {rows.length > visible.length && (
            <span>
              {visible.length} atelier{visible.length > 1 ? 's' : ''} affiché
              {visible.length > 1 ? 's' : ''} sur {rows.length}
            </span>
          )}
          {excludedCount > 0 && (
            <span>
              {excludedCount} atelier{excludedCount > 1 ? 's' : ''} proche
              {excludedCount > 1 ? 's' : ''} exclu
              {excludedCount > 1 ? 's' : ''} : réservations fermées ou atelier
              inactif.
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={() => navigate('/workshops')}
          className="text-sm text-[#623413] hover:underline"
        >
          Voir tous les ateliers
        </button>
      </div>
    </section>
  );
};

export default UpcomingFillPanel;
